'use client';

import useGlobalState from '@hooks/useGlobalState';
import Text from '@wconcept/wconcept-ui-renewal/src/components/atoms/Text';
import Box from '@wconcept/wconcept-ui-renewal/src/components/atoms/Box';
import Button from '@wconcept/wconcept-ui-renewal/src/components/atoms/Button';
import Title from '@wconcept/wconcept-ui-renewal/src/components/atoms/Title';
import { Suspense, useCallback } from 'react';
import TooltipBox from '@wconcept/wconcept-ui-renewal/src/components/molecules/TooltipBox';
import RecoStylingList from '@wconcept/wconcept-ui-renewal/src/components/organisms/_list/RecoStylingList';
import RecoStylingItem from '@wconcept/wconcept-ui-renewal/src/components/molecules/_item/RecoStylingItem';
import { SearchStyleClip } from '@wconcept/wconcept-common/models/renewal/search';
import RecoStylingSkeleton from '@wconcept/wconcept-ui-renewal/src/components/molecules/_skeleton/RecoStylingSkeleton';
import ComponentRefreshBtn from '@wconcept/wconcept-ui-renewal/src/components/molecules/_btn/ComponentRefreshBtn';

function RecoStyleClipList({
  onStyleClipClick,
  onClickStyleClipMore,
  styleClipData,
  isLoading,
  error,
  refetch,
}: {
  onStyleClipClick: (value: SearchStyleClip, i: number) => void;
  onClickStyleClipMore: () => void;
  styleClipData: SearchStyleClip[];
  isLoading: boolean;
  error: Error | null;
  refetch: () => void;
}) {
  const onClickStyleClip = useCallback(
    (value: SearchStyleClip, i: number) => {
      onStyleClipClick(value, i);
    },
    [onStyleClipClick]
  );

  const onClickMore = useCallback(() => {
    onClickStyleClipMore?.();
  }, [onClickStyleClipMore]);

  const getItems = useCallback(
    () =>
      styleClipData?.map((v: SearchStyleClip, i) => (
        <RecoStylingItem
          key={v.id}
          username={v.profileInfo?.profile?.nickName}
          vid={v?.vodInfo?.id}
          videoPoster={v?.vodInfo?.thumbnailUrl}
          token={{ playAuthToken: v.vodInfo?.token as string }}
          onClick={() => onClickStyleClip(v, i)}
          priority={i < 4}
        />
      )),
    [onClickStyleClip, styleClipData]
  );

  const getContent = useCallback(() => {
    if (isLoading) {
      return <RecoStylingSkeleton />;
    }
    if (error) {
      return <ComponentRefreshBtn onClick={() => refetch()} />;
    }
    if (!styleClipData || styleClipData.length === 0) {
      return null;
    }
    return <RecoStylingList>{getItems()}</RecoStylingList>;
  }, [error, getItems, isLoading, refetch, styleClipData]);

  // 데이터가 3개 미만이면 렌더링하지 않음 (로딩 중이어도 렌더링)
  if (!isLoading && (!styleClipData || styleClipData.length < 3)) return null;

  return (
    <Box as="section" className="ly-full corner-recommend-styling">
      <Box>
        <Title
          className="main-title"
          level={2}
          suffix={
            <Button onClick={onClickMore}>
              <Text className="sub-title">더보기</Text>
            </Button>
          }
        >
          추천 스타일링
          <TooltipBox
            type="left"
            text="고객님이 선호하는 상품, 콘텐츠와 <br /> 연관된 스타일링 영상을 추천해 드립니다."
          />
        </Title>
      </Box>
      <Suspense fallback={<RecoStylingSkeleton />}>{getContent()}</Suspense>
    </Box>
  );
}

export default RecoStyleClipList;
